import type { ParseKeys, TFunction } from 'i18next';

export type DateRangeEnum =
  | 'LAST_7D'
  | 'LAST_14D'
  | 'LAST_30D'
  | 'LAST_90D'
  | 'CUSTOM';

export const DATE_RANGE_VALUES = [
  'LAST_7D',
  'LAST_14D',
  'LAST_30D',
  'LAST_90D',
  'CUSTOM',
] as const satisfies readonly DateRangeEnum[];

export interface DateRangeParams {
  date_range: DateRangeEnum;
  start_date?: string;
  end_date?: string;
}

const DAYS_BY_PRESET: Record<Exclude<DateRangeEnum, 'CUSTOM'>, number> = {
  LAST_7D: 7,
  LAST_14D: 14,
  LAST_30D: 30,
  LAST_90D: 90,
};

// The period selector's own pill copy, so a heading names the window in the same words
// the marketer just clicked.
const PRESET_LABEL_KEYS = {
  LAST_7D: 'period_selector.last_7d',
  LAST_14D: 'period_selector.last_14d',
  LAST_30D: 'period_selector.last_30d',
  LAST_90D: 'period_selector.last_90d',
} as const satisfies Record<
  Exclude<DateRangeEnum, 'CUSTOM'>,
  ParseKeys<'dashboard'>
>;

// CUSTOM keeps its raw ISO pair: that is what `period_selector.custom_range` shows too.
export const rangeLabel = (
  range: DateRangeParams,
  t: TFunction<'dashboard'>,
): string => {
  if (range.date_range === 'CUSTOM') {
    return `${range.start_date ?? ''} – ${range.end_date ?? ''}`;
  }
  return t(PRESET_LABEL_KEYS[range.date_range]);
};

const ONE_DAY_MS = 86_400_000;

// Format as YYYY-MM-DD using the user's *local* date components, not UTC.
// `toISOString()` would silently shift the day for users west of UTC after
// ~16:00 local, making "previous window" off by one day.
const toIso = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDays = (d: Date, days: number): Date =>
  new Date(d.getTime() + days * ONE_DAY_MS);

const previousFromPreset = (
  days: number,
  now: Date,
): { start_date: string; end_date: string } => {
  const previousEnd = shiftDays(now, -days - 1);
  const previousStart = shiftDays(previousEnd, -(days - 1));
  return {
    start_date: toIso(previousStart),
    end_date: toIso(previousEnd),
  };
};

const previousFromCustom = (
  start: string,
  end: string,
): { start_date: string; end_date: string } => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const lengthDays =
    Math.round((endDate.getTime() - startDate.getTime()) / ONE_DAY_MS) + 1;
  const previousEnd = shiftDays(startDate, -1);
  const previousStart = shiftDays(previousEnd, -(lengthDays - 1));
  return {
    start_date: toIso(previousStart),
    end_date: toIso(previousEnd),
  };
};

// Inclusive [today - days, today] window, formatted with local date parts.
// Used to seed empty date-range filters so dashboards never load blank.
export const defaultDateRange = (
  days: number,
  now: Date = new Date(),
): { start: string; end: string } => ({
  start: toIso(shiftDays(now, -days)),
  end: toIso(now),
});

// Local-date-safe conversions shared with the date picker.
export const toIsoDate = (d: Date): string => toIso(d);

export const parseIsoDate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

export const todayIso = (now: Date = new Date()): string => toIso(now);

// Calendar arithmetic in UTC, so neither the viewer's zone nor a DST change moves the day.
const shiftIsoDays = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// The CUSTOM range as long as `preset`, ending on the day `end`.
export const rangeEndingOn = (
  preset: Exclude<DateRangeEnum, 'CUSTOM'>,
  end: string,
): Required<DateRangeParams> => ({
  date_range: 'CUSTOM',
  start_date: shiftIsoDays(end, -(DAYS_BY_PRESET[preset] - 1)),
  end_date: end,
});

export const previousWindowOf = (
  range: DateRangeParams,
  now: Date = new Date(),
): DateRangeParams => {
  if (range.date_range !== 'CUSTOM') {
    const days = DAYS_BY_PRESET[range.date_range];
    return { date_range: 'CUSTOM', ...previousFromPreset(days, now) };
  }

  if (!range.start_date || !range.end_date) {
    return { date_range: 'CUSTOM' };
  }

  return {
    date_range: 'CUSTOM',
    ...previousFromCustom(range.start_date, range.end_date),
  };
};
